import SoundCloudUser from './soundCloudUser';

/**
 * What playback is allowed to do with a track, for the signed-in user in
 * their country. `preview` is the 30-second clip; `blocked` cannot be played
 * at all. Filter search with `access=playable,preview` and show the badge.
 */
export type SoundCloudAccess = 'playable' | 'preview' | 'blocked';

/**
 * A track from api.soundcloud.com. Shapes from the OpenAPI spec at
 * github.com/soundcloud/api; the fields Fadila reads are typed strictly and
 * the rest loosely.
 *
 * There is no artist field. SoundCloud's model is uploader-centric: `user`
 * is who put it up, and `metadata_artist` is only sometimes set by labels.
 */
export default interface SoundCloudApiTrack {
  /** `soundcloud:tracks:123` - what every endpoint addresses the track by. */
  urn: string;
  /** Numeric id, deprecated in favour of the URN. */
  id?: number;
  kind?: 'track';
  title: string;
  description?: string | null;
  /** Milliseconds. */
  duration: number;
  genre?: string | null;
  tag_list?: string | null;
  label_name?: string | null;
  license?: string | null;
  /**
   * The track's page on soundcloud.com. The API terms want a visible link
   * back from every track shown, so this is not optional for us.
   */
  permalink_url: string;
  permalink?: string;
  artwork_url?: string | null;
  waveform_url?: string | null;
  /** `2015/10/10 12:21:08 +0000` - see parseSoundCloudDate. */
  created_at: string;
  last_modified?: string;
  release_year?: number | null;
  release_month?: number | null;
  release_day?: number | null;
  release?: string | null;
  access: SoundCloudAccess;
  streamable?: boolean;
  downloadable?: boolean;
  sharing?: 'public' | 'private';
  embeddable_by?: string;
  /** Deprecated and preview-only. Ignore; `/tracks/{urn}/streams` is the way. */
  stream_url?: string;
  playback_count?: number;
  favoritings_count?: number;
  likes_count?: number;
  comment_count?: number;
  reposts_count?: number;
  metadata_artist?: string | null;
  bpm?: number | null;
  key_signature?: string | null;
  isrc?: string | null;
  purchase_url?: string | null;
  purchase_title?: string | null;
  user: SoundCloudUser;
  user_urn?: string;
}

/**
 * SoundCloud writes dates as `2015/10/10 12:21:08 +0000`, which `new Date()`
 * reads fine in Chrome and as `Invalid Date` in Safari. Newer responses use
 * ISO 8601; both come through here.
 */
export function parseSoundCloudDate(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }

  const match = /^(\d{4})[/-](\d{2})[/-](\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(value);

  if (match) {
    return Date.UTC(+match[1], +match[2] - 1, +match[3], +match[4], +match[5], +match[6]);
  }

  const parsed = Date.parse(value);

  return isNaN(parsed) ? 0 : parsed;
}
